import { useCallback, useState } from "react";
import { AppointmentReportResendDialogProps } from ".";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import useDefaultErrorWrapper from "../../../hooks/useDefaultErrorWrapper";

const useAppointmentReportResendDialog = (params: {
  appointmentId: string;
  patientEmail: string;
  patientFirstName: string;
  patientLastName: string;
  refetch: () => Promise<void>;
}): {
  props: AppointmentReportResendDialogProps | null;
  isOpened: boolean;
  onOpenClick: () => void;
} => {
  const {
    appointmentId,
    patientEmail,
    patientFirstName,
    patientLastName,
    refetch,
  } = params;
  const [isLoading, setIsLoading] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { runAsyncFunction } = useDefaultErrorWrapper();
  const resendAppointmentReport = useFirebaseAppFunction(
    "resendAppointmentReport",
  );

  const onOpenClick = () => setIsOpened(true);
  const handleClose = () => {
    setIsOpened(false);
    setErrorMessage(null);
  };

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    try {
      await runAsyncFunction(resendAppointmentReport, {
        appointmentId,
      });

      await refetch();
      handleClose();
    } catch (error) {
      setErrorMessage((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [appointmentId, refetch, runAsyncFunction, resendAppointmentReport]);

  return {
    isOpened,
    onOpenClick,
    props: {
      isOpened,
      isLoading,
      patientEmail,
      patientFirstName,
      patientLastName,
      errorMessage,
      handleClose,
      handleSubmit,
    },
  };
};

export default useAppointmentReportResendDialog;
